import { useEffect } from "react";

import { MuiChipsInput } from "mui-chips-input";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { PortfolioInput, PortfolioIntegrationType } from "@graphql/api";
import {
  Form,
  FormCheckbox,
  FormRadioGroup,
  FormStateField,
  FormTextField,
  FormZipcodeField,
  SubmitButton,
} from "@covertree/ui";

import {
  portfolioConfigurationFormInitialValues,
  PortfolioConfigurationFormValues,
  validationSchemaForPortfolioConfigurationForm,
} from "./portfolioConfigurationForm";
import { useAddPortfolioMutation } from "@redux/portfolios/portfoliosApi";
import { Toast } from "@covertree/utils";
import { useNavigate } from "react-router-dom";
import {
  ADD_PORTFOLIO_ROUTES,
  BASE_PORTFOLIOS_PATH,
  PORTFOLIO_ROUTES,
  PORTFOLIOS_ROUTES,
} from "@routing/Portfolios";

async function addPortfolioViaLocalShim(portfolio: PortfolioInput) {
  const response = await fetch(
    "http://localhost:8002/handler/addNewPortfolio",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        arguments: {
          portfolio,
        },
      }),
    }
  );

  const body = await response.json();

  if (!response.ok || body !== true) {
    throw new Error(
      typeof body?.message === "string"
        ? body.message
        : `Local addNewPortfolio failed (${response.status})`
    );
  }
}

export function PortfolioConfiguration() {
  const navigate = useNavigate();
  const [addPortfolio, { isLoading }] = useAddPortfolioMutation();

  const form = useForm({
    defaultValues: portfolioConfigurationFormInitialValues,
    mode: "all",
    resolver: yupResolver(validationSchemaForPortfolioConfigurationForm),
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValid },
    setValue,
    trigger,
    watch,
  } = form;

  const minUnderReviewTimePeriodInDays = watch(
    "minUnderReviewTimePeriodInDays"
  );
  const secondDocumentReminderDelayInDays = watch(
    "secondDocumentReminderDelayInDays"
  );
  const thirdDocumentReminderDelayInDays = watch(
    "thirdDocumentReminderDelayInDays"
  );
  const disableSendInitialDocumentsValue = watch(
    "disableSendInitialDocumentUploadReminders"
  );
  const disableSendSecondDocumentValue = watch(
    "disableSendSecondDocumentReminder"
  );
  const disableSendThirdDocumentValue = watch(
    "disableSendThirdDocumentReminder"
  );

  useEffect(() => {
    if (disableSendInitialDocumentsValue) {
      // @ts-expect-error We want to clear out the value
      setValue("firstDocumentReminderDelayInDays", "");
    } else {
      setValue("firstDocumentReminderDelayInDays", 0);
    }
  }, [disableSendInitialDocumentsValue]);

  useEffect(() => {
    if (disableSendSecondDocumentValue) {
      // @ts-expect-error We want to clear out the value
      setValue("secondDocumentReminderDelayInDays", "");
      setValue("disableSendThirdDocumentReminder", true);
    } else {
      const [
        underReviewDays,
        disableSendThird,
        secondDelayDays,
        thirdDelayDays,
      ] = getValues([
        "minUnderReviewTimePeriodInDays",
        "disableSendThirdDocumentReminder",
        "secondDocumentReminderDelayInDays",
        "thirdDocumentReminderDelayInDays",
      ]);

      if (
        secondDelayDays ===
        portfolioConfigurationFormInitialValues.secondDocumentReminderDelayInDays
      ) {
        return;
      }

      if (!disableSendThird && thirdDelayDays) {
        setValue(
          "secondDocumentReminderDelayInDays",
          Math.max(
            thirdDelayDays - 1,
            portfolioConfigurationFormInitialValues.secondDocumentReminderDelayInDays
          )
        );
        return;
      }

      setValue(
        "secondDocumentReminderDelayInDays",
        Math.min(
          underReviewDays - 1,
          portfolioConfigurationFormInitialValues.secondDocumentReminderDelayInDays
        )
      );
    }
  }, [disableSendSecondDocumentValue]);

  useEffect(() => {
    if (disableSendThirdDocumentValue) {
      // @ts-expect-error We want to clear out the value
      setValue("thirdDocumentReminderDelayInDays", "");
    } else {
      const [underReviewDays, thirdDelayDays] = getValues([
        "minUnderReviewTimePeriodInDays",
        "thirdDocumentReminderDelayInDays",
      ]);

      if (
        thirdDelayDays ===
        portfolioConfigurationFormInitialValues.thirdDocumentReminderDelayInDays
      ) {
        return;
      }

      setValue(
        "thirdDocumentReminderDelayInDays",
        Math.min(
          underReviewDays - 1,
          portfolioConfigurationFormInitialValues.thirdDocumentReminderDelayInDays
        )
      );
    }
  }, [disableSendThirdDocumentValue]);

  useEffect(() => {
    trigger([
      "firstDocumentReminderDelayInDays",
      "secondDocumentReminderDelayInDays",
      "thirdDocumentReminderDelayInDays",
    ]);
  }, [minUnderReviewTimePeriodInDays]);

  useEffect(() => {
    trigger("thirdDocumentReminderDelayInDays");
  }, [secondDocumentReminderDelayInDays]);

  useEffect(() => {
    trigger("secondDocumentReminderDelayInDays");
  }, [thirdDocumentReminderDelayInDays]);

  const onSubmit = async (formData: PortfolioConfigurationFormValues) => {
    const {
      addressLine2,
      disableSendThirdDocumentReminder,
      disableSendSecondDocumentReminder,
      disableSendInitialDocumentUploadReminders,
      firstDocumentReminderDelayInDays,
      secondDocumentReminderDelayInDays,
      thirdDocumentReminderDelayInDays,
      id,
      state,
      ...restFormData
    } = formData;

    const portfolioId = id?.trim();

    const portfolio: PortfolioInput = {
      ...restFormData,
      ...(!addressLine2 || addressLine2.length === 0 ? {} : { addressLine2 }),
      ...(portfolioId ? { id: portfolioId } : {}),
      firstDocumentReminderDelayInDays: disableSendInitialDocumentsValue
        ? null
        : firstDocumentReminderDelayInDays,
      sendInitialDocumentUploadReminders:
        !disableSendInitialDocumentUploadReminders,
      secondDocumentReminderDelayInDays: disableSendSecondDocumentReminder
        ? -1
        : secondDocumentReminderDelayInDays!,
      thirdDocumentReminderDelayInDays: disableSendThirdDocumentReminder
        ? -1
        : thirdDocumentReminderDelayInDays!,
      state: state.value,
    };

    const error = await (async () => {
      try {
        if (process.env.VITE_ENV_NAME === "local") {
          await addPortfolioViaLocalShim(portfolio);
          return undefined;
        }

        const result = await addPortfolio({
          portfolio,
        });

        if (result.error) return result.error;
        if (result.data !== true) {
          return [{ message: "addNewPortfolio returned false" }];
        }
        return undefined;
      } catch (err) {
        return [
          {
            message: err instanceof Error ? err.message : String(err),
          },
        ];
      }
    })();

    if (error) {
      console.error("Portfolio creation failed", { error });
      Toast.showError(
        "There was an error while creating Portfolio. Please get in touch with Maple tech team before you continue"
      );
      return;
    }

    navigate(
      `/${BASE_PORTFOLIOS_PATH}/${PORTFOLIOS_ROUTES.PORTFOLIO}/${PORTFOLIO_ROUTES.ADD}/${ADD_PORTFOLIO_ROUTES.CONFIGURATION_SUCCESS}`,
      { replace: true, state: portfolio }
    );
  };

  return (
    <Grid
      container
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyItems="flex-start"
      gap={3}>
      <FormProvider {...form}>
        <Form style={{ width: "100%" }} onSubmit={handleSubmit(onSubmit)}>
          <Stack gap={2}>
            <Stack
              border="1px solid"
              borderRadius="10px"
              padding={3}
              borderColor="action.disabled">
              <Typography variant="h4">General Details</Typography>
              <Stack>
                <FormTextField
                  id="name"
                  name="name"
                  label="Name"
                  control={control}
                  disabled={isSubmitting || isLoading}
                />
                <FormTextField
                  id="portfolioId"
                  name="id"
                  label="Portfolio ID"
                  control={control}
                  disabled={isSubmitting || isLoading}
                  optional
                  tooltip="Optional. Must be a valid UUID. Leave empty to have the ID generated automatically."
                />
                <FormTextField
                  id="managersName"
                  name="managersName"
                  label="Managers Names"
                  control={control}
                  disabled={isSubmitting || isLoading}
                  tooltip="This field is used when sending emails to Portfolio Managers."
                />
                <Box mt={2} flex={1} sx={{ width: "100%" }}>
                  <Controller
                    name="emailAddresses"
                    control={control}
                    render={({ field, fieldState }) => (
                      <MuiChipsInput
                        {...field}
                        disabled={isSubmitting || isLoading}
                        InputProps={{
                          sx: {
                            "& input": {
                              padding: "0 !important",
                              paddingLeft: "10px !important",
                              marginTop: "-10px !important",
                            },
                            "& legend": {
                              display: "none",
                            },
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          sx: (theme) => ({
                            color: `${theme.colors.grey1} !important`,
                            position: "static",
                            alignItems: "flex-end",
                            display: "flex",
                            height: "auto",
                            transform: "none",
                            "& > p": {
                              display: "flex",
                              alignItems: "center",
                              flex: 1,
                              width: "100%",
                              whiteSpace: "break-spaces",
                            },
                          }),
                        }}
                        variant="outlined"
                        label="Managers email addresses"
                        fullWidth
                        hideClearAll
                        helperText={
                          Array.isArray(fieldState.error)
                            ? fieldState.error.find((err) => !!err)?.message
                            : fieldState.error?.message
                        }
                        error={fieldState.invalid}
                      />
                    )}
                  />
                </Box>
              </Stack>
            </Stack>
            <Stack
              border="1px solid"
              borderRadius="10px"
              padding={3}
              borderColor="action.disabled">
              <Typography variant="h4">Address details</Typography>
              <FormTextField
                id="addressLine1"
                name="addressLine1"
                label="Address Line 1"
                control={control}
                disabled={isSubmitting || isLoading}
              />
              <FormTextField
                id="addressLine2"
                name="addressLine2"
                label="Address Line 2"
                control={control}
                disabled={isSubmitting || isLoading}
                optional
              />
              <FormTextField
                id="city"
                name="city"
                label="City"
                control={control}
                disabled={isSubmitting || isLoading}
              />
              <FormTextField
                id="county"
                name="county"
                label="County"
                control={control}
                disabled={isSubmitting || isLoading}
              />
              <FormStateField
                id="state"
                name="state"
                label="State"
                control={control}
                disabled={isSubmitting || isLoading}
              />
              <FormZipcodeField
                id="zipCode"
                name="zipCode"
                label="Zip Code"
                control={control}
                disabled={isSubmitting || isLoading}
              />
            </Stack>
            <Stack
              border="1px solid"
              borderRadius="10px"
              padding={3}
              borderColor="action.disabled">
              <Typography variant="h4">Configuration details</Typography>
              <FormTextField
                id="minUnderReviewTimePeriodInDays"
                name="minUnderReviewTimePeriodInDays"
                label="Minimum days in under review"
                type="number"
                control={control}
                disabled={isSubmitting || isLoading}
              />
              <Box mt={3}>
                <FormRadioGroup
                  name="createResidentHubspotLeadOnImport"
                  control={control}
                  loading={isSubmitting || isLoading}
                  label="Should create Hubspot lead on upload?"
                  selected={watch("createResidentHubspotLeadOnImport")}
                  items={[
                    { title: "Yes", value: true },
                    { title: "No", value: false },
                  ]}
                />
              </Box>
              <Stack direction="row" spacing={2} alignItems="end">
                <FormTextField
                  id="firstDocumentReminderDelayInDays"
                  name="firstDocumentReminderDelayInDays"
                  label="First notification delay (days)"
                  type="number"
                  control={control}
                  disabled={
                    isSubmitting ||
                    isLoading ||
                    disableSendInitialDocumentsValue
                  }
                />
                <Box pb={1}>
                  <FormCheckbox
                    id="disableSendInitialDocumentUploadReminders"
                    name="disableSendInitialDocumentUploadReminders"
                    control={control}
                    label="Disable?"
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="end">
                <FormTextField
                  id="secondDocumentReminderDelayInDays"
                  name="secondDocumentReminderDelayInDays"
                  label="Second notification delay (days)"
                  type="number"
                  control={control}
                  disabled={
                    isSubmitting || isLoading || disableSendSecondDocumentValue
                  }
                />
                <Box pb={1}>
                  <FormCheckbox
                    id="disableSendSecondDocumentReminder"
                    name="disableSendSecondDocumentReminder"
                    control={control}
                    disabled={isSubmitting || isLoading}
                    label="Disable?"
                  />
                </Box>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="end">
                <FormTextField
                  id="thirdDocumentReminderDelayInDays"
                  name="thirdDocumentReminderDelayInDays"
                  label="Third notification delay (days)"
                  type="number"
                  control={control}
                  disabled={
                    isSubmitting || isLoading || disableSendThirdDocumentValue
                  }
                />
                <Box pb={1}>
                  <FormCheckbox
                    id="disableSendThirdDocumentReminder"
                    name="disableSendThirdDocumentReminder"
                    control={control}
                    disabled={
                      isSubmitting ||
                      isLoading ||
                      disableSendSecondDocumentValue
                    }
                    label="Disable?"
                  />
                </Box>
              </Stack>
              <FormTextField
                id="delayToStartUnderReviewTimePeriodAfterCancellationOrExpirationInDays"
                name="delayToStartUnderReviewTimePeriodAfterCancellationOrExpirationInDays"
                label="Delay under review start after cancellation/expiration (days)"
                type="number"
                control={control}
                disabled={isSubmitting || isLoading}
              />
            </Stack>
            <Stack
              border="1px solid"
              borderRadius="10px"
              padding={3}
              borderColor="action.disabled">
              <Typography variant="h4">Integration details</Typography>
              <FormRadioGroup
                name="integrationType"
                control={control}
                loading={isSubmitting || isLoading}
                label="Integration Type"
                selected={watch("integrationType")}
                items={[
                  {
                    title: "Rent Manager",
                    value: PortfolioIntegrationType.RentManager,
                  },
                ]}
              />
              <FormTextField
                id="integrationId"
                name="integrationId"
                label="ID of Portfolio in the external system"
                control={control}
                disabled={isSubmitting || isLoading}
                tooltip="For RentManager - it's CORPID."
              />
              <Box mt={3}>
                <FormRadioGroup
                  name="syncAddressData"
                  control={control}
                  loading={isSubmitting || isLoading}
                  label="Should grab location data from Precisely?"
                  selected={watch("syncAddressData")}
                  items={[
                    { title: "Yes", value: true },
                    { title: "No", value: false },
                  ]}
                />
              </Box>
            </Stack>
          </Stack>
          <SubmitButton
            fullWidth={false}
            loading={isSubmitting || isLoading}
            disabled={!isValid || isSubmitting || isLoading}
            width={200}>
            Next
          </SubmitButton>
        </Form>
      </FormProvider>
    </Grid>
  );
}
